/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import DatePickerInput from '@/domain/vertraege/features/components/DatePickerFormField/DatePickerInput';
import { useSchema } from '@/domain/vertraege/features/VertragKuendigen/useSchema';
import { useVertraegeKuendigen } from '@/domain/vertraege/shared/hooks/useVertraegeKuendigen';
import { KuendigungsGrund } from '@/domain/vertraege/shared/types/kuendigungsGrund';
import { Vertrag } from '@/domain/vertraege/shared/types/vertrag';
import { mapKuendigungsGrund } from '@/domain/vertraege/shared/utils/kuendigungsGrundMapper';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z from 'zod';

export function VertragKuendigen({ vertrag }: { vertrag: Vertrag }) {
  const { t } = useTranslation();
  const { manager } = useProfile();
  const [vertraegeKuendigen, { loading }] = useVertraegeKuendigen();
  const [open, setOpen] = useState(false);

  const formSchema = useSchema({ t });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vorname: manager.vorname,
      nachname: manager.nachname,
      kundennummer: 'K-' + vertrag.kundennummer.toString(),
      vertragsnummer: 'W-' + vertrag.id.toString(),
      unternehmen: vertrag.unternehmensname,
      email: manager.email,
      telefon: '',
      grund: undefined,
      kuendigungZum: undefined,
      beschreibung: '',
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    vertraegeKuendigen({
      input: {
        beschreibung: values.beschreibung,
        kuendigungZum: values.kuendigungZum.toISOString(),
        kuendigungVersendetAm: dayjs().toISOString(),
        telefon: values.telefon,
        grund: mapKuendigungsGrund(values.grund),
        vertragsId: values.vertragsnummer.split('-')[1],
        kundennummer: values.kundennummer.split('-')[1],
        nachname: values.nachname,
        vorname: values.vorname,
        unternehmen: values.unternehmen,
      },
    }).then((result) => {
      if (result.data?.vertraege_kuendigen === true) {
        toast({
          description: t('domain.vertraege.kuendigenForm.submit.success'),
        });
        setOpen(false);
        form.reset();
      } else {
        toast({
          description: t('domain.vertraege.kuendigenForm.submit.error'),
        });
      }
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline-secondary">
          {t('domain.vertraege.kuendigung.button')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('domain.vertraege.kuendigenForm.title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 mt-4">
            <div className="flex gap-4 w-full">
              <FormField
                control={form.control}
                name="vorname"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t('domain.vertraege.kuendigenForm.fields.vorname.label')}
                    </FormLabel>
                    <Input
                      value={field.value}
                      disabled
                      type="text"
                      id="vorname"
                      autoComplete="off"
                      data-1p-ignore
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nachname"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t(
                        'domain.vertraege.kuendigenForm.fields.nachname.label',
                      )}
                    </FormLabel>
                    <Input
                      {...field}
                      disabled
                      type="text"
                      id="nachname"
                      autoComplete="off"
                      data-1p-ignore
                    />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="unternehmen"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>
                    {t(
                      'domain.vertraege.kuendigenForm.fields.unternehmen.label',
                    )}
                  </FormLabel>
                  <Input
                    {...field}
                    type="text"
                    id="unternehmen"
                    autoComplete="off"
                    data-1p-ignore
                    disabled
                  />
                </FormItem>
              )}
            />
            <div className="flex gap-4 w-full">
              <FormField
                control={form.control}
                name="kundennummer"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t(
                        'domain.vertraege.kuendigenForm.fields.kundennummer.label',
                      )}
                    </FormLabel>
                    <Input
                      {...field}
                      type="text"
                      id="kundennummer"
                      autoComplete="off"
                      data-1p-ignore
                      disabled
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="vertragsnummer"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t(
                        'domain.vertraege.kuendigenForm.fields.vertragsnummer.label',
                      )}
                    </FormLabel>
                    <Input
                      {...field}
                      type="text"
                      id="vertragsnummer"
                      autoComplete="off"
                      data-1p-ignore
                      disabled
                    />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex gap-4 w-full">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t('domain.vertraege.kuendigenForm.fields.email.label')}
                    </FormLabel>
                    <Input
                      {...field}
                      type="text"
                      id="email"
                      autoComplete="off"
                      data-1p-ignore
                      disabled
                    />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="telefon"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t('domain.vertraege.kuendigenForm.fields.telefon.label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Telefonnummer eingeben"
                        type="text"
                        id="telefon"
                        autoComplete="off"
                        data-1p-ignore
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex gap-4 w-full">
              <FormField
                control={form.control}
                name="grund"
                render={({ field }) => (
                  <FormItem className="flex-1" key={field.value}>
                    <FormLabel>
                      {t('domain.vertraege.kuendigenForm.fields.grund.label')}
                    </FormLabel>
                    <FormControl className="w-full">
                      <Select
                        value={field.value}
                        defaultValue={field.value}
                        onValueChange={field.onChange}
                        {...field}>
                        <SelectTrigger
                          className={`w-full border-neutrals-N300 ${
                            !field.value
                              ? 'text-neutrals-N700'
                              : 'text-neutrals-N900'
                          }`}>
                          <SelectValue placeholder="Grund auswählen">
                            {field.value
                              ? t(
                                  `domain.vertraege.kuendigenForm.fields.grund.select.${field.value}`,
                                )
                              : undefined}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="border-neutrals-N300">
                          <SelectGroup>
                            {Object.values(KuendigungsGrund).map(
                              (enumValue) => (
                                <SelectItem key={enumValue} value={enumValue}>
                                  {t(
                                    `domain.vertraege.kuendigenForm.fields.grund.select.${enumValue}`,
                                  )}
                                </SelectItem>
                              ),
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="kuendigungZum"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>
                      {t(
                        'domain.vertraege.kuendigenForm.fields.kuendigungZum.label',
                      )}
                    </FormLabel>
                    <FormControl className="w-full">
                      <DatePickerInput
                        {...field}
                        onSelect={(d) => field.onChange(d)}
                        date={field.value ? dayjs(field.value) : undefined}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>
                      {t(
                        'domain.vertraege.kuendigenForm.fields.kuendigungZum.description',
                      )}
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="beschreibung"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      'domain.vertraege.kuendigenForm.fields.beschreibung.label',
                    )}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      id="beschreibung"
                      autoComplete="off"
                      data-1p-ignore
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end mt-4">
              <Button type="submit" disabled={loading}>
                {t('domain.vertraege.kuendigenForm.submit.label')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
